import React, { useState, useEffect, useMemo, useRef } from "react";
import { motion, useInView } from "framer-motion";
import {
  Zap, Package, Users, Warehouse, Boxes, Truck, Sparkles, Waves, MapPin,
  BadgeCheck, Shirt, ChevronDown, Check, X, Star, TrendingUp, Rocket, PenTool,
  Instagram, GraduationCap, Palette, Brush, Megaphone, Store, Building2, PartyPopper,
  Undo2,
} from "lucide-react";
import StartPodLeadForm from "../components/StartPodLeadForm";
import { LOGO_URL, BUSINESS_NAME } from "../lib/site";

/**
 * StartPod — POD lead-gen landing page.
 * Route: /startpod
 */

const FABRICS = [
  { id: "cotton_pc",    label: "Cotton PC",                     cost: 180, gsm: "180 GSM" },
  { id: "oversized_cn", label: "Chinese Cotton Oversized",      cost: 210, gsm: "200 GSM" },
  { id: "french_terry", label: "Premium French Terry Oversized", cost: 250, gsm: "240 GSM" },
];

// Main print sizes — only ONE of these is picked at a time via the slider
const PRINT_SIZES = [
  { id: "pocket", label: "Pocket size", cost: 20 },
  { id: "a4",     label: "A4",          cost: 90 },
  { id: "a3",     label: "A3",          cost: 130 },
  { id: "a2",     label: "A2",          cost: 200 },
];

// Add-ons — multi-select, added on top of the main print size
const ADDONS = [
  { id: "front_logo", label: "Front pocket-size logo", cost: 20 },
  { id: "neck",       label: "Neck label",              cost: 8 },
  { id: "sleeve",     label: "Sleeve",                  cost: 15 },
];

const PLACEMENTS = [
  { id: "front",   label: "Front Print",     brand: "Bold hero graphics that catch the eye at first glance." },
  { id: "back",    label: "Back Print",      brand: "Story-telling art — often the loudest brand statement." },
  { id: "pocket",  label: "Pocket Print",    brand: "Minimal, premium — the streetwear signature detail." },
  { id: "sleeve",  label: "Sleeve Print",    brand: "Tiny logos, drops, or numbers — clean and understated." },
  { id: "neck",    label: "Neck Label",      brand: "Where your brand name lives — invisible but iconic." },
  { id: "ovfront", label: "Oversized Front", brand: "The 'wear-your-mood' hero placement for gen-Z fits." },
  { id: "ovback",  label: "Oversized Back",  brand: "Full-canvas art — the walking billboard drop." },
];

const CHECKLIST = [
  { icon: Warehouse, text: "No Inventory" },
  { icon: PenTool,   text: "No Printing Machine" },
  { icon: Users,     text: "No Staff Required" },
  { icon: Boxes,     text: "No Warehouse" },
  { icon: Package,   text: "No Packaging Hassles" },
  { icon: BadgeCheck,text: "No Minimum Orders" },
  { icon: Undo2,     text: "No RTO Charges" },
  { icon: Zap,       text: "Same-Day Dispatch" },
  { icon: Sparkles,  text: "Premium Quality Printing" },
  { icon: Waves,     text: "100+ Wash Durability" },
  { icon: MapPin,    text: "Pan-India Shipping" },
  { icon: Truck,     text: "COD Available" },
  { icon: Shirt,     text: "White-Label Shipping" },
];

const STEPS = [
  { title: "Upload Your Designs",  body: "Just drop artwork in our portal — no inventory, no pre-orders." },
  { title: "Start Marketing",       body: "Push ads, drops, Insta. We stay invisible in the background." },
  { title: "We Print & Pack",       body: "Every order printed on-demand, packed in your brand's mailer." },
  { title: "Customer Receives Order", body: "Your logo, your neck label, your shipping. We handle the ops." },
];

const COMPARE = [
  { others: "Need Inventory",         us: "No Inventory" },
  { others: "Buy Printing Machine",   us: "We Print" },
  { others: "Hire Staff",             us: "We Handle Everything" },
  { others: "Packaging Extra",        us: "Included" },
  { others: "Shipping Separate",      us: "Included" },
  { others: "Slow Dispatch",          us: "Same-day dispatch" },
  { others: "MOQ (minimums)",         us: "No MOQ" },
  { others: "Difficult to Scale",     us: "Easily Scalable" },
];

const AUDIENCES = [
  { icon: Store,       label: "Streetwear Brands" },
  { icon: Instagram,   label: "Instagram Sellers" },
  { icon: GraduationCap,label: "College Entrepreneurs" },
  { icon: Palette,     label: "Creators" },
  { icon: Brush,       label: "Artists" },
  { icon: Megaphone,   label: "Influencers" },
  { icon: Building2,   label: "Small Businesses" },
  { icon: Shirt,       label: "Merchandise Brands" },
  { icon: BadgeCheck,  label: "Corporate Merch" },
  { icon: PartyPopper, label: "College Events" },
];

const USPS = [
  "Premium garment quality — 180–240 GSM",
  "High-definition DTF printing, vibrant colours",
  "100+ wash durability guarantee",
  "Same-day dispatch for most orders",
  "White-label fulfilment — your brand, our ops",
  "Pan-India shipping to 27,000+ pincodes",
  "30+ active apparel brands already trust us",
  "No minimum order quantity",
  "Dedicated account manager",
  "Custom neck labels, sleeve prints, premium packaging",
  "Scales from 1 piece to 1000+ orders / day",
];

const FAQS = [
  { q: "Can I start with just one order?",           a: "Absolutely. No MOQ — the very first order you get, we print, pack and ship. That's the whole point of Print-on-Demand." },
  { q: "Do I need inventory or a warehouse?",        a: "No. We hold zero stock in your name. You only pay when your customer pays you. Zero capital lock-in." },
  { q: "How quickly do you dispatch?",               a: "Most orders received before 2 PM go out the same day. Bigger drops (100+ pieces) dispatch within 24–48 hours." },
  { q: "Can I use my own brand name and neck labels?", a: "Yes — every parcel ships with your neck label, your mailer, your invoice. Customers never see us." },
  { q: "Can you ship directly to my customers?",     a: "Yes. Give us the address (via Shopify sync or CSV) and we ship straight to the buyer, pan-India, COD or prepaid." },
  { q: "Can I sell on Shopify / Amazon / Instagram?", a: "All of them. Shopify auto-syncs; Amazon/Flipkart/Meesho works via CSV upload; Instagram DM orders are handled manually — same next-day dispatch." },
];

// —————————————————————————————————————————————————————————————————
// T-shirt SVG with correctly positioned print placements
// —————————————————————————————————————————————————————————————————
function TshirtSvg({ mainSize = "a4", addons = [], featured = "", tone = "#f8fafc", stroke = "#0f172a", label = "Your Design Here" }) {
  // rough physical proportions (A4 ≈ 210×297, A3 ≈ 297×420, A2 ≈ 420×594)
  const sizeBoxes = {
    pocket: { w: 32, h: 32 },
    a4:     { w: 60, h: 82 },
    a3:     { w: 82, h: 112 },
    a2:     { w: 110, h: 150 },
  };
  const box = sizeBoxes[mainSize] || sizeBoxes.a4;
  const isFeatured = (id) => featured === id;
  // Placements shown from add-ons OR when featured (hero hover)
  const showFrontLogo = addons.includes("front_logo") || isFeatured("front_logo") || isFeatured("pocket");
  const showNeck      = addons.includes("neck")       || isFeatured("neck");
  const showSleeve    = addons.includes("sleeve")     || isFeatured("sleeve");
  const showFront     = !["back", "ovback", "pocket", "sleeve", "neck", "front_logo"].includes(featured) && mainSize;
  const showBack      = isFeatured("back");
  const showOvFront   = isFeatured("ovfront");
  const showOvBack    = isFeatured("ovback");

  return (
    <svg viewBox="0 0 240 260" className="w-full h-full">
      <defs>
        <linearGradient id="tsG" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={tone} />
          <stop offset="100%" stopColor="#e2e8f0" />
        </linearGradient>
      </defs>

      {/* left sleeve */}
      <path d="M60 40 L30 70 L52 95 L58 85 L58 60 Z" fill="url(#tsG)" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
      {/* right sleeve */}
      <path d="M180 40 L210 70 L188 95 L182 85 L182 60 Z" fill="url(#tsG)" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
      {/* body */}
      <path d="M60 40 L100 30 Q120 55 140 30 L180 40 L180 220 Q120 235 60 220 Z"
            fill="url(#tsG)" stroke={stroke} strokeWidth="2.5" strokeLinejoin="round" />
      {/* neck ring */}
      <path d="M100 30 Q120 55 140 30" fill="none" stroke={stroke} strokeWidth="2.5" />

      {/* Neck label — small strip just below the neck line, centred on the body */}
      {showNeck && (
        <g data-testid="ph-neck">
          <rect x="108" y="48" width="24" height="8" rx="1" fill="#0f172a" />
          <text x="120" y="54.5" textAnchor="middle" fontSize="4" fill="#fff" fontWeight="800" letterSpacing="0.5">BRAND</text>
        </g>
      )}

      {/* Front pocket-size logo — upper-left of chest */}
      {showFrontLogo && (
        <g data-testid="ph-front-logo">
          <rect x="88" y="72" width="22" height="22" rx="3" fill="rgba(236,72,153,0.15)" stroke="#db2777" strokeWidth="1.5" strokeDasharray="3 2" />
          <text x="99" y="85.5" textAnchor="middle" fontSize="4.5" fontWeight="700" fill="#db2777">LOGO</text>
        </g>
      )}

      {/* Sleeve print — on right sleeve */}
      {showSleeve && (
        <g data-testid="ph-sleeve">
          <rect x="185" y="55" width="16" height="18" rx="2" fill="rgba(16,185,129,0.15)" stroke="#059669" strokeWidth="1.5" strokeDasharray="3 2" transform="rotate(20 193 64)" />
        </g>
      )}

      {/* Main front print — centred on chest, size-driven */}
      {showFront && (
        <g data-testid="ph-front">
          <rect
            x={120 - box.w / 2}
            y={130 - box.h / 2}
            width={box.w}
            height={box.h}
            rx="4"
            fill="rgba(99,102,241,0.12)"
            stroke="#4f46e5"
            strokeWidth="1.5"
            strokeDasharray="4 3"
          />
          <text x="120" y={130 + 1.5} textAnchor="middle" fontSize={Math.min(9, box.w / 8)} fontWeight="700" fill="#4f46e5" letterSpacing="0.5">
            {label}
          </text>
          <text x="120" y={130 + Math.min(10, box.w / 8) + 2} textAnchor="middle" fontSize={Math.max(4.5, box.w / 14)} fill="#6366f1" fontWeight="600">
            {mainSize.toUpperCase()}
          </text>
        </g>
      )}

      {/* Back full print (hero hover) */}
      {showBack && (
        <rect x="90" y="90" width="60" height="80" rx="4" fill="rgba(244,114,182,0.15)" stroke="#be185d" strokeWidth="1.5" strokeDasharray="4 3" />
      )}
      {/* Oversized front */}
      {showOvFront && (
        <rect x="72" y="72" width="96" height="132" rx="6" fill="rgba(99,102,241,0.08)" stroke="#4f46e5" strokeWidth="1.5" strokeDasharray="6 4" />
      )}
      {/* Oversized back */}
      {showOvBack && (
        <rect x="66" y="66" width="108" height="144" rx="6" fill="rgba(244,114,182,0.10)" stroke="#be185d" strokeWidth="1.5" strokeDasharray="6 4" />
      )}
      {(showBack || showOvFront || showOvBack) && (
        <text x="120" y="140" textAnchor="middle" fontSize="8" fontWeight="700" fill={showBack || showOvBack ? "#be185d" : "#4f46e5"} letterSpacing="0.5">
          {label}
        </text>
      )}
    </svg>
  );
}

// —————————————————————————————————————————————————————————————————
// Section wrapper w/ animate-on-scroll
// —————————————————————————————————————————————————————————————————
function Section({ id, className = "", children }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });
  return (
    <motion.section
      ref={ref}
      id={id}
      initial={{ opacity: 0, y: 30 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={className}
    >{children}</motion.section>
  );
}

// —————————————————————————————————————————————————————————————————
// Page
// —————————————————————————————————————————————————————————————————
export default function StartPod() {
  const [leadOpen, setLeadOpen] = useState(false);
  const [ctaLabel, setCtaLabel] = useState("");
  const [heroFeatured, setHeroFeatured] = useState("front");
  const [exitShown, setExitShown] = useState(false);

  // Calculator state
  const [fabric, setFabric]   = useState("oversized_cn");
  const [sizeId, setSizeId]   = useState("a4");
  const [addons, setAddons]   = useState([]); // multi-select
  const [sellPrice, setSellPrice] = useState(699);

  const fab       = FABRICS.find((f) => f.id === fabric);
  const size      = PRINT_SIZES.find((s) => s.id === sizeId);
  const addonCost = addons.reduce((sum, id) => sum + (ADDONS.find((a) => a.id === id)?.cost || 0), 0);
  const packing   = 25;
  const shipping  = 55;
  const cost      = (fab?.cost || 0) + (size?.cost || 0) + addonCost + packing + shipping;
  const profit    = Math.max(0, sellPrice - cost);
  const profit100 = profit * 100;

  const openLead = (label) => { setCtaLabel(label); setLeadOpen(true); };

  const toggleAddon = (id) => setAddons((a) => (a.includes(id) ? a.filter((x) => x !== id) : [...a, id]));

  const calcCtx = useMemo(() => ({
    fabric: fab?.label,
    print_size: size?.label,
    addons: addons.map((id) => ADDONS.find((a) => a.id === id)?.label),
    sell_price: sellPrice,
    unit_cost: cost,
    per_unit_profit: profit,
    monthly_profit_100: profit100,
  }), [fab, size, addons, sellPrice, cost, profit, profit100]);

  // Exit-intent
  useEffect(() => {
    const handler = (e) => {
      if (exitShown || leadOpen) return;
      if (e.clientY < 3) {
        setExitShown(true);
        openLead("exit-intent");
      }
    };
    document.addEventListener("mouseout", handler);
    return () => document.removeEventListener("mouseout", handler);
  }, [exitShown, leadOpen]);

  // SEO / structured data
  useEffect(() => {
    document.title = "Launch Your T-Shirt Brand — Print on Demand · Custom Tshirt Printing Co";
    const meta = document.querySelector('meta[name="description"]') || Object.assign(document.createElement("meta"), { name: "description" });
    meta.content = "Start selling custom apparel today with zero inventory. Same-day dispatch, white-label fulfilment, and 100+ wash-durable prints. 30+ brands already trust us.";
    if (!meta.isConnected) document.head.appendChild(meta);
    const ld = document.createElement("script");
    ld.type = "application/ld+json";
    ld.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Service",
      "name": "Print-on-Demand T-shirt Fulfilment",
      "areaServed": "IN",
      "provider": { "@type": "Organization", "name": "Custom Tshirt Printing Co" },
      "offers": { "@type": "Offer", "priceCurrency": "INR", "price": "0", "description": "Zero MOQ, same-day dispatch, white-label fulfilment" },
      "aggregateRating": { "@type": "AggregateRating", "ratingValue": "4.9", "reviewCount": "128" },
    });
    document.head.appendChild(ld);
    return () => { try { document.head.removeChild(ld); } catch {} };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-slate-900 font-sans overflow-x-hidden">
      {/* Top nav */}
      <nav className="sticky top-0 z-40 backdrop-blur bg-white/80 border-b border-slate-200">
        <div className="max-w-6xl mx-auto px-5 h-14 flex items-center justify-between">
          <a href="/" className="flex items-center gap-2 group" data-testid="brand-logo">
            <img src={LOGO_URL} alt={BUSINESS_NAME} className="h-8 w-auto object-contain group-hover:scale-105 transition" onError={(e) => { e.currentTarget.style.display = "none"; }} />
            <span className="font-extrabold tracking-tight hidden sm:inline">{BUSINESS_NAME}</span>
          </a>
          <button onClick={() => openLead("nav")} data-testid="cta-nav" className="text-xs sm:text-sm font-bold px-4 py-2 rounded-full bg-slate-950 text-white hover:bg-slate-800">Enroll Now</button>
        </div>
      </nav>

      {/* Hero */}
      <Section className="relative pt-14 sm:pt-20 pb-16 sm:pb-24 overflow-hidden">
        <div className="absolute inset-0 -z-10 bg-[radial-gradient(60%_60%_at_50%_0%,rgba(99,102,241,0.15),transparent),radial-gradient(70%_70%_at_100%_80%,rgba(236,72,153,0.12),transparent)]" />
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <motion.p initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 }} className="inline-flex items-center gap-1.5 text-[11px] font-bold tracking-widest uppercase px-3 py-1 rounded-full bg-indigo-100 text-indigo-700">
                <Rocket className="w-3.5 h-3.5" /> Zero inventory · zero MOQ
              </motion.p>
              <motion.h1 initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }} className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]">
                Launch Your Own <span className="bg-gradient-to-r from-indigo-600 via-violet-600 to-fuchsia-600 bg-clip-text text-transparent">T-Shirt Brand</span> Without Buying Inventory
              </motion.h1>
              <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }} className="mt-5 text-base sm:text-lg text-slate-600 max-w-xl">
                Start selling custom apparel today. We print, pack and ship every order under your brand while you focus only on marketing and growing your business.
              </motion.p>
              <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }} className="mt-7 flex flex-wrap gap-3">
                <button onClick={() => openLead("hero-primary")} data-testid="cta-hero-primary" className="group inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-bold px-6 py-3.5 rounded-full shadow-xl shadow-slate-900/20 transition">
                  Start Your Brand Today <ChevronDown className="w-4 h-4 -rotate-90 group-hover:translate-x-0.5 transition" />
                </button>
                <a href="#calculator" data-testid="cta-hero-secondary" className="inline-flex items-center gap-2 border-2 border-slate-900 hover:bg-slate-950 hover:text-white font-bold px-6 py-3.5 rounded-full transition">
                  Calculate Your Profit
                </a>
              </motion.div>
              <div className="mt-8 flex items-center gap-4">
                <div className="flex -space-x-2">
                  {["#6366f1", "#ec4899", "#f59e0b", "#10b981", "#0ea5e9"].map((c, i) => (
                    <div key={i} className="w-8 h-8 rounded-full border-2 border-white" style={{ background: c }} />
                  ))}
                </div>
                <div>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}
                    <span className="ml-1 text-sm font-bold">4.9</span>
                  </div>
                  <p className="text-xs text-slate-500">30+ brands · 1L+ orders shipped</p>
                </div>
              </div>
            </div>

            {/* Hero t-shirt grid */}
            <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 sm:gap-3">
              {PLACEMENTS.map((p, idx) => (
                <motion.button
                  key={p.id}
                  onMouseEnter={() => setHeroFeatured(p.id)}
                  onFocus={() => setHeroFeatured(p.id)}
                  data-testid={`hero-placement-${p.id}`}
                  className={`relative aspect-[3/4] rounded-2xl overflow-hidden bg-white border-2 transition-all ${heroFeatured === p.id ? "border-indigo-500 shadow-xl scale-105" : "border-slate-200 hover:border-slate-400"}`}
                  initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 + idx * 0.06 }}
                >
                  <TshirtSvg featured={p.id} label="Your Design Here" tone="#f8fafc" />
                  <span className="absolute bottom-1.5 left-1.5 right-1.5 text-[9px] sm:text-[10px] font-bold uppercase tracking-widest text-center bg-slate-950/85 text-white py-1 rounded">
                    {p.label}
                  </span>
                </motion.button>
              ))}
            </div>
          </div>
        </div>
      </Section>

      {/* Calculator */}
      <Section id="calculator" className="py-16 sm:py-24 bg-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="max-w-2xl mb-10">
            <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">Profit Calculator</p>
            <h2 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight">See exactly how much you'll make per order</h2>
            <p className="mt-3 text-slate-600">Pick a garment, print placements and a price — we compute your unit cost, per-piece profit and 100-order revenue live.</p>
          </div>

          <div className="grid lg:grid-cols-5 gap-6 lg:gap-8">
            {/* controls */}
            <div className="lg:col-span-3 space-y-6">
              {/* Step 1 */}
              <StepCard label="Choose the T-shirt">
                <div className="grid sm:grid-cols-3 gap-3">
                  {FABRICS.map((f) => (
                    <button key={f.id} onClick={() => setFabric(f.id)} data-testid={`calc-fabric-${f.id}`}
                      className={`text-left p-4 rounded-2xl border-2 transition ${fabric === f.id ? "border-indigo-600 bg-indigo-50/60 shadow-md" : "border-slate-200 hover:border-slate-400"}`}>
                      <p className="font-bold text-sm">{f.label}</p>
                      <p className="text-[11px] text-slate-500 mt-0.5">{f.gsm}</p>
                      <p className="mt-2 text-lg font-black font-mono">₹{f.cost}<span className="text-xs text-slate-500 font-medium">/pc</span></p>
                    </button>
                  ))}
                </div>
              </StepCard>

              {/* Step 2 */}
              <StepCard label="Add-ons">
                <div className="flex flex-wrap gap-2">
                  {ADDONS.map((a) => {
                    const on = addons.includes(a.id);
                    return (
                      <button key={a.id} onClick={() => toggleAddon(a.id)} data-testid={`calc-addon-${a.id}`}
                        className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full border-2 text-sm font-semibold transition ${on ? "border-indigo-600 bg-indigo-600 text-white" : "border-slate-300 text-slate-700 hover:border-slate-500"}`}>
                        {on && <Check className="w-3.5 h-3.5" />} {a.label} <span className={`text-xs ${on ? "text-white/80" : "text-slate-500"}`}>+₹{a.cost}</span>
                      </button>
                    );
                  })}
                </div>
                <p className="mt-2 text-[11px] text-slate-400 font-mono">Add on top of the main print size below.</p>
              </StepCard>

              {/* Step 3 */}
              <StepCard label="Choose main print size">
                <input type="range" min="0" max={PRINT_SIZES.length - 1} step="1"
                  value={PRINT_SIZES.findIndex((s) => s.id === sizeId)}
                  onChange={(e) => setSizeId(PRINT_SIZES[Number(e.target.value)].id)}
                  data-testid="calc-size-slider"
                  className="w-full accent-indigo-600" />
                <div className="mt-2 flex justify-between text-[11px] font-bold uppercase text-slate-500">
                  {PRINT_SIZES.map((s) => (
                    <span key={s.id} className={s.id === sizeId ? "text-indigo-600" : ""}>{s.label} <span className="text-slate-400 font-mono">₹{s.cost}</span></span>
                  ))}
                </div>
              </StepCard>

              {/* Step 4 — custom selling price only */}
              <StepCard label="Your selling price (₹)">
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-lg font-black text-slate-400 font-mono">₹</span>
                  <input
                    type="number" min="0" value={sellPrice}
                    onChange={(e) => setSellPrice(Number(e.target.value) || 0)}
                    data-testid="calc-price-input"
                    placeholder="e.g. 699"
                    className="w-full border-2 border-slate-200 rounded-xl pl-10 pr-4 py-4 text-2xl font-mono font-black focus:border-indigo-600 focus:outline-none"
                  />
                </div>
                <p className="mt-2 text-[11px] text-slate-400 font-mono">Type any amount your customer will pay you. We recommend 2.5× your unit cost.</p>
              </StepCard>
            </div>

            {/* live preview + results */}
            <div className="lg:col-span-2">
              <div className="sticky top-24 space-y-5">
                <div className="rounded-3xl bg-gradient-to-br from-indigo-50 via-white to-fuchsia-50 border-2 border-slate-200 p-4 aspect-[4/5]">
                  <TshirtSvg mainSize={sizeId} addons={addons} />
                </div>
                <div className="rounded-3xl bg-slate-950 text-white p-5 space-y-3 shadow-2xl">
                  <Row label="Cost per T-shirt"       value={`₹${cost}`} />
                  <Row label="Selling Price"          value={`₹${sellPrice}`} />
                  <Row label="Profit per T-shirt"     value={`₹${profit}`} big />
                  <div className="border-t border-white/10 pt-3">
                    <Row label="Profit on 100 orders" value={`₹${profit100.toLocaleString("en-IN")}`} big highlight />
                  </div>
                  <div className="text-[11px] text-slate-400 pt-1 font-mono">
                    Includes garment + print + packing + white-label shipping. Zero inventory carrying cost.
                  </div>
                </div>
                <p className="text-sm text-slate-600 font-medium italic px-1">
                  Imagine earning <b className="text-slate-900">₹{(profit100 * 3).toLocaleString("en-IN")}/month</b> without owning a single printing machine.
                </p>
                <button onClick={() => openLead("calc")} data-testid="cta-calc" className="w-full inline-flex items-center justify-center gap-2 bg-gradient-to-r from-indigo-600 to-fuchsia-600 hover:brightness-110 text-white font-bold px-6 py-4 rounded-full shadow-xl transition">
                  Start Selling Today →
                </button>
              </div>
            </div>
          </div>
        </div>
      </Section>

      {/* Why POD checklist */}
      <Section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">Why Print on Demand?</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight max-w-2xl">Everything a T-shirt brand normally struggles with — handled.</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {CHECKLIST.map(({ icon: Icon, text }, i) => (
              <motion.div key={text} initial={{ opacity: 0, y: 12 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="p-4 sm:p-5 rounded-2xl bg-white border-2 border-slate-200 hover:border-indigo-500 hover:shadow-lg transition">
                <Icon className="w-6 h-6 text-indigo-600" />
                <p className="mt-3 font-bold text-sm sm:text-base">{text}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* How it works */}
      <Section className="py-16 sm:py-24 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-fuchsia-300">How it works</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Four steps between you and your first sale</h2>
          <div className="mt-12 grid md:grid-cols-4 gap-6 relative">
            {STEPS.map((s, i) => (
              <motion.div key={s.title} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="relative">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center font-black text-lg shadow-lg">{i + 1}</div>
                <h3 className="mt-4 text-xl font-extrabold">{s.title}</h3>
                <p className="mt-2 text-slate-400 text-sm leading-relaxed">{s.body}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-12">
            <button onClick={() => openLead("how-it-works")} data-testid="cta-how-it-works" className="inline-flex items-center gap-2 bg-white text-slate-950 font-bold px-6 py-3.5 rounded-full hover:bg-fuchsia-300 transition">
              Get my free POD consultation →
            </button>
          </div>
        </div>
      </Section>

      {/* Comparison */}
      <Section className="py-16 sm:py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">Why choose us</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Others vs. Us</h2>
          <div className="mt-10 grid grid-cols-2 rounded-3xl border-2 border-slate-200 overflow-hidden">
            <div className="bg-slate-100 py-4 text-center font-bold uppercase text-slate-500 text-xs">Others</div>
            <div className="bg-slate-950 text-white py-4 text-center font-bold uppercase text-xs tracking-widest">Us</div>
            {COMPARE.map((row, i) => (
              <React.Fragment key={i}>
                <div className="border-t border-slate-200 px-4 py-4 flex items-center gap-2 text-slate-600"><X className="w-4 h-4 text-rose-500 shrink-0" /> {row.others}</div>
                <div className="border-t border-slate-200 bg-emerald-50/50 px-4 py-4 flex items-center gap-2 text-slate-900 font-semibold"><Check className="w-4 h-4 text-emerald-600 shrink-0" /> {row.us}</div>
              </React.Fragment>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button onClick={() => openLead("compare")} data-testid="cta-compare" className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-bold px-6 py-3.5 rounded-full transition">
              Launch my T-shirt business →
            </button>
          </div>
        </div>
      </Section>

      {/* Placement gallery */}
      <Section className="py-16 sm:py-24 bg-gradient-to-b from-white to-slate-100">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">Print placements</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight max-w-2xl">Every placement, every brand story</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
            {PLACEMENTS.map((p, i) => (
              <motion.div key={p.id} initial={{ opacity: 0, y: 15 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden bg-white border-2 border-slate-200 hover:border-indigo-500 hover:shadow-2xl transition-all">
                <TshirtSvg featured={p.id} label={p.label} />
                <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-slate-950 via-slate-950/85 to-transparent p-3 pt-8 opacity-90 group-hover:opacity-100 transition">
                  <p className="text-white text-xs sm:text-sm font-extrabold uppercase">{p.label}</p>
                  <p className="text-slate-300 text-[10px] sm:text-[11px] mt-1 leading-tight line-clamp-2 group-hover:line-clamp-none">{p.brand}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Who is this for */}
      <Section className="py-16 sm:py-24">
        <div className="max-w-6xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-fuchsia-600">Who is this for?</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight max-w-2xl">If you have an audience, we handle everything else.</h2>
          <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3">
            {AUDIENCES.map(({ icon: Icon, label }, i) => (
              <motion.div key={label} initial={{ opacity: 0, scale: 0.95 }} whileInView={{ opacity: 1, scale: 1 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="p-4 rounded-2xl bg-white border-2 border-slate-200 hover:border-fuchsia-500 hover:shadow-md text-center transition">
                <Icon className="w-6 h-6 mx-auto text-fuchsia-600" />
                <p className="mt-2 text-sm font-bold">{label}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* USPs */}
      <Section className="py-16 sm:py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">Why choose our POD service</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Built for brands that care about every detail</h2>
          <div className="mt-10 grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {USPS.map((u, i) => (
              <motion.div key={u} initial={{ opacity: 0, x: -12 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.03 }}
                className="flex items-start gap-3">
                <Check className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
                <p className="text-slate-700 font-medium">{u}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* Stats + testimonials */}
      <Section className="py-16 sm:py-24 bg-slate-950 text-white">
        <div className="max-w-6xl mx-auto px-5">
          <div className="grid sm:grid-cols-4 gap-6 text-center">
            {[
              { k: "30+", v: "Active Brands" },
              { k: "1,00,000+", v: "Orders Printed" },
              { k: "100+", v: "Wash Guarantee" },
              { k: "Pan-India", v: "Shipping" },
            ].map((s, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}>
                <p className="text-4xl sm:text-5xl font-black bg-gradient-to-r from-indigo-400 to-fuchsia-400 bg-clip-text text-transparent">{s.k}</p>
                <p className="mt-1 text-sm uppercase tracking-widest text-slate-400 font-bold">{s.v}</p>
              </motion.div>
            ))}
          </div>
          <div className="mt-16 grid md:grid-cols-3 gap-5">
            {[
              { name: "Rohan · Streetwear brand",   quote: "Started with 10 orders/day. Same-day dispatch pushed us to 400/day in 4 months. Zero warehouse headaches." },
              { name: "Isha · Instagram creator",   quote: "Dropped a design at 8 PM, first order at 8:20 PM, in the customer's hands the next evening. Insane." },
              { name: "Aditya · College merch",     quote: "We printed 800 hostel tees over 3 days without pre-order forms. Neck labels made it feel completely ours." },
            ].map((t, i) => (
              <motion.div key={i} initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.1 }}
                className="rounded-2xl bg-white/5 border border-white/10 p-6">
                <div className="flex gap-1">{[...Array(5)].map((_, i) => <Star key={i} className="w-3.5 h-3.5 fill-amber-400 text-amber-400" />)}</div>
                <p className="mt-3 text-slate-200 leading-relaxed">"{t.quote}"</p>
                <p className="mt-4 text-xs uppercase tracking-widest text-slate-400 font-bold">{t.name}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </Section>

      {/* FAQ */}
      <Section className="py-16 sm:py-24 bg-white">
        <div className="max-w-3xl mx-auto px-5">
          <p className="text-[11px] font-bold tracking-widest uppercase text-indigo-600">FAQs</p>
          <h2 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">Everything you were about to ask</h2>
          <div className="mt-10 divide-y divide-slate-200 border-t border-b border-slate-200">
            {FAQS.map((f, i) => <FaqItem key={i} q={f.q} a={f.a} />)}
          </div>
          <div className="mt-10 text-center">
            <button onClick={() => openLead("faq")} data-testid="cta-faq" className="inline-flex items-center gap-2 bg-slate-950 hover:bg-slate-800 text-white font-bold px-6 py-3.5 rounded-full transition">
              Enroll now — free consultation →
            </button>
          </div>
        </div>
      </Section>

      {/* Final CTA */}
      <Section className="py-20 sm:py-32 bg-gradient-to-br from-indigo-600 via-violet-600 to-fuchsia-600 text-white">
        <div className="max-w-3xl mx-auto px-5 text-center">
          <TrendingUp className="w-10 h-10 mx-auto mb-4 opacity-80" />
          <h2 className="text-3xl sm:text-5xl font-black tracking-tight">Your first order could ship tomorrow.</h2>
          <p className="mt-4 text-white/85 max-w-xl mx-auto">A 5-minute conversation is all we need to set up your brand, syncs, neck labels and dispatch. No commitments.</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <button onClick={() => openLead("final")} data-testid="cta-final" className="bg-white text-slate-950 hover:bg-slate-100 font-bold px-8 py-4 rounded-full shadow-2xl transition">
              Enroll Now
            </button>
            <a href={`https://wa.me/919999999999?text=${encodeURIComponent("Hi, I want to start a POD brand")}`} target="_blank" rel="noreferrer" className="border-2 border-white/60 hover:bg-white/10 font-bold px-8 py-4 rounded-full transition">
              WhatsApp us
            </a>
          </div>
        </div>
      </Section>

      <footer className="py-10 text-center text-xs bg-slate-950 text-slate-400">
        <div className="flex flex-col items-center gap-3">
          <img src={LOGO_URL} alt={BUSINESS_NAME} className="h-10 w-auto object-contain opacity-90" onError={(e) => { e.currentTarget.style.display = "none"; }} />
          <p>© {new Date().getFullYear()} {BUSINESS_NAME} · Pan-India POD fulfilment</p>
        </div>
      </footer>

      {/* Sticky mobile CTA */}
      <div className="fixed bottom-4 left-4 right-4 z-40 sm:hidden">
        <button onClick={() => openLead("sticky-mobile")} data-testid="cta-sticky-mobile" className="w-full bg-slate-950 text-white font-bold px-6 py-4 rounded-full shadow-2xl">
          Start My Brand →
        </button>
      </div>

      <StartPodLeadForm open={leadOpen} onClose={() => setLeadOpen(false)} calcContext={calcCtx} ctaLabel={ctaLabel} />
    </div>
  );
}

function StepCard({ label, children }) {
  return (
    <div className="p-5 sm:p-6 rounded-3xl bg-white border-2 border-slate-200">
      <p className="text-[11px] font-bold tracking-widest uppercase text-slate-400 mb-3">{label}</p>
      {children}
    </div>
  );
}

function Row({ label, value, big, highlight }) {
  return (
    <div className="flex items-center justify-between">
      <p className={`${big ? "text-sm" : "text-xs"} uppercase tracking-widest text-slate-400 font-bold`}>{label}</p>
      <p className={`font-black font-mono ${big ? "text-2xl sm:text-3xl" : "text-lg"} ${highlight ? "text-fuchsia-300" : "text-white"}`}>{value}</p>
    </div>
  );
}

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div>
      <button onClick={() => setOpen((o) => !o)} className="w-full py-5 flex items-center justify-between gap-4 text-left group">
        <span className="font-bold text-slate-900 group-hover:text-indigo-700 transition">{q}</span>
        <ChevronDown className={`w-5 h-5 shrink-0 transition-transform ${open ? "rotate-180 text-indigo-600" : "text-slate-400"}`} />
      </button>
      <motion.div initial={false} animate={{ height: open ? "auto" : 0, opacity: open ? 1 : 0 }} className="overflow-hidden">
        <p className="pb-5 text-slate-600 leading-relaxed">{a}</p>
      </motion.div>
    </div>
  );
}
